import { createMembersAreaWidgetPluginService } from '@wix/members-area-widget-plugin-lib/viewer';
import { createEventHandler } from '@wix/tpa-settings';
import type { ControllerParams } from '@wix/yoshi-flow-editor';

import { getAccountData } from '../../server/account.api';
import type { Store } from '../../store';
import { createStore, getActionHandlers } from '../../store';
import { localeThunk } from '../../store/slices';
import type {
  ControllerContext,
  ControllerProps,
  FlowApi,
  ToastState,
  WixCodeApi,
} from '../../types';
import { Toast } from '../../types';
import type { SettingsEvent } from '../../types/appSettings';
import type { AccountData } from '../../types/server';
import type { ProfileInfoChangeSubject } from '../../viewer.app';
import { registerControllerListeners } from './controller-listeners';
import { withLocaleHeadersFactory } from './controllerUtils/localeHeaders';

export const getControllerContext = ({
  flowAPI,
  controllerConfig: { setProps, wixCodeApi },
  appData,
}: ControllerParams): ControllerContext => {
  const profileInfoChangeSubject = appData?.profileInfoChangeSubject as
    | ProfileInfoChangeSubject
    | undefined;
  const settingsListener = createEventHandler<SettingsEvent>({});
  const widgetPluginService = createMembersAreaWidgetPluginService();

  return {
    flowAPI,
    wixCodeApi,
    setProps,
    profileInfoChangeSubject,
    settingsListener,
    widgetPluginService,
  };
};

const getControllerProps = (flowAPI: FlowApi, store: Store) => {
  const state = store.getState();
  const actionHandlers = getActionHandlers(store);
  const props: ControllerProps = {
    ...state,
    ...actionHandlers,
    ...(flowAPI.controllerConfig.platformAPIs.bi?.viewerName ===
      'thunderbolt' && { fitToContentHeight: true }),
    isRendered: true,
  };

  return props;
};

const getInitialState = async (wixCodeApi: WixCodeApi, flowAPI: FlowApi) => {
  const withLocaleHeaders = withLocaleHeadersFactory(flowAPI);
  const { isEditor } = flowAPI.environment;
  const { data } = await flowAPI.httpClient.request<AccountData>(
    withLocaleHeaders(getAccountData({ isEditor })),
  );

  const { member, fields, site } = data;

  return {
    fields,
    member,
    site: {
      baseUrl: wixCodeApi.location.baseUrl,
      showPublicSections: site?.showPublicSections ?? false,
      isSSOMandatory: site?.isSSOMandatory ?? false,
      enabledSocialConnections: site?.enabledSocialConnections ?? [],
      memberProfileURL: site?.memberProfileURL ?? '',
      isAboutInstalled: site?.isAboutInstalled ?? false,
    },
    ui: {
      toast: {
        isVisible: false,
        type: Toast.NONE,
        options: {},
      } as ToastState,
      slug: {
        isLoading: false,
        isAvailable: true,
      },
      dropdownOptions: {
        country: [],
        state: [],
      },
      shouldOpenBlockedModal:
        wixCodeApi.location.query.blockedMembersModal === 'true',
      changePasswordState: { stage: 'initial' as const },
      changeEmailState: { stage: 'initial' as const },
    },
  };
};

export const initializeMyAccountWidget = async ({
  wixCodeApi,
  flowAPI,
  setProps,
  settingsListener,
  profileInfoChangeSubject,
}: ControllerContext) => {
  if (wixCodeApi.user.currentUser.loggedIn === false) {
    flowAPI.sentry?.captureMessage('User not logged in');
    return;
  }

  const store = createStore(
    {
      flowAPI,
      wixCodeApi,
      profileInfoChangeSubject,
    },
    await getInitialState(wixCodeApi, flowAPI),
  );
  store.subscribe(() => setProps({ ...store.getState() }));

  const props = getControllerProps(flowAPI, store);
  setProps(props);
  store.dispatch(localeThunk.fetchLocaleData());
  await registerControllerListeners(flowAPI, settingsListener, store);
};
